import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, Trash2, ArrowLeft, Loader2, Feather } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useProcessing } from '../../context/ProcessingContext';
import { Alert } from '../../components/Alert';
import { UploadedFileItem } from '../../components/UploadedFileItem';
import { useValidatedTextarea } from '../../hooks/useValidatedTextarea';
import { useNavigate, Link } from 'react-router-dom';
import clsx from 'clsx';
import DOMPurify from 'dompurify';
import JSZip from 'jszip';
import { supabase } from '../../lib/supabase';
import { getFeatureCreditCostCache, setFeatureCreditCostCache } from '../../lib/cache';
import { cleanTextForSubmission } from '../../hooks/useValidatedTextarea';

interface UploadedFile {
  id: string;
  file: File;
  preview: string;
  description: string;
  generatedFileName: string;
  isValid: boolean;
}

// Magic numbers for image validation
const IMAGE_MAGIC_NUMBERS = {
  'image/png': [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A], // PNG
  'image/jpeg': [0xFF, 0xD8, 0xFF], // JPEG/JPG
};

const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB
const MAX_FILES = 10;

// Generate random alphanumeric string
function generateAlphanumericString(length: number): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

// Generate filename with preserved extension
function generateFileName(originalFileName: string): string {
  const extension = originalFileName.split('.').pop() || '';
  const randomName = generateAlphanumericString(10);
  return extension ? `${randomName}.${extension}` : randomName;
}

export function PaperAssistant() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [creditCost, setCreditCost] = useState<number>(0);
  const [featureId, setFeatureId] = useState<string | null>(null);

  // Use the validated textarea hook for research insights
  const {
    value: insightText,
    wordCount: insightWordCount,
    error: insightError,
    onChange: onInsightChange,
    validate: validateInsight
  } = useValidatedTextarea({
    initialValue: '',
    minWords: 100,
    maxWords: 1000,
    maxChars: 10000,
    maxPunctuationRatio: 0.5,
    maxConsecutivePunctuation: 20,
    required: false
  });

  const [globalError, setGlobalError] = useState<string | null>(null);

  const {
    isProcessing,
    setIsProcessing,
    setProcessingMessage,
    resetProcessingState
  } = useProcessing();

  // Fetch credit cost and feature ID on component mount
  useEffect(() => {
    if (!user) return;

    const fetchCreditCost = async () => {
      const cachedCreditCost = getFeatureCreditCostCache('paper-assistant');
      if (cachedCreditCost !== null) {
        setCreditCost(cachedCreditCost);
        return;
      }

      const { data, error } = await supabase
        .from('features')
        .select('id, credit_cost')
        .eq('slug', 'paper-assistant')
        .single();

      if (!error && data) {
        setCreditCost(data.credit_cost);
        setFeatureId(data.id);
        setFeatureCreditCostCache('paper-assistant', data.credit_cost);
      }
    };

    fetchCreditCost();
  }, [user]);

  const validateFileContent = async (file: File): Promise<string | null> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (reader.result instanceof ArrayBuffer) {
          const byteArray = new Uint8Array(reader.result);

          // Check for PNG magic number
          const pngMagic = IMAGE_MAGIC_NUMBERS['image/png'];
          if (byteArray.length >= pngMagic.length && pngMagic.every((val, i) => byteArray[i] === val)) {
            resolve(null); // Valid PNG
            return;
          }

          // Check for JPEG magic number
          const jpegMagic = IMAGE_MAGIC_NUMBERS['image/jpeg'];
          if (byteArray.length >= jpegMagic.length && jpegMagic.every((val, i) => byteArray[i] === val)) {
            resolve(null); // Valid JPEG
            return;
          }
        }
        resolve('Invalid image file. Only PNG and JPEG/JPG formats are supported.');
      };
      reader.onerror = () => {
        resolve('Failed to read file content.');
      };
      reader.readAsArrayBuffer(file.slice(0, 8)); // Read first 8 bytes for magic number check
    });
  };

  const onDrop = useCallback(async (acceptedFiles: File[], fileRejections: any[]) => {
    setGlobalError(null); // Clear previous errors

    if (fileRejections.length > 0) {
      const rejection = fileRejections[0];
      if (rejection.errors && rejection.errors.length > 0) {
        const error = rejection.errors[0];
        if (error.code === 'file-too-large') {
          setGlobalError('File is too large. Maximum size is 1MB.');
        } else if (error.code === 'file-invalid-type') {
          setGlobalError('Invalid file type. Only PNG and JPEG/JPG images are allowed.');
        } else if (error.code === 'too-many-files') {
          setGlobalError(`You can upload a maximum of ${MAX_FILES} images.`);
        } else {
          setGlobalError('An unknown error occurred during file selection.');
        }
      }
      return;
    }

    if (acceptedFiles.length === 0) {
      return;
    }

    if (uploadedFiles.length + acceptedFiles.length > MAX_FILES) {
      setGlobalError(`You can upload a maximum of ${MAX_FILES} images.`);
      return;
    }

    setIsProcessing(true);
    setProcessingMessage('Validating files...');

    const newFiles: UploadedFile[] = [];
    for (const file of acceptedFiles) {
      const contentError = await validateFileContent(file);
      if (contentError) {
        setGlobalError(contentError);
        resetProcessingState();
        return;
      }

      newFiles.push({
        id: crypto.randomUUID(),
        file,
        preview: URL.createObjectURL(file),
        description: '',
        generatedFileName: generateFileName(file.name),
        isValid: true,
      });
    }

    setUploadedFiles((prevFiles) => [...prevFiles, ...newFiles]);
    resetProcessingState();
  }, [uploadedFiles, setIsProcessing, setProcessingMessage, resetProcessingState]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpeg', '.jpg'],
    },
    maxFiles: MAX_FILES,
    maxSize: MAX_FILE_SIZE,
    multiple: true,
    disabled: isProcessing,
  });

  const handleDescriptionChange = useCallback((id: string, description: string, isValid: boolean) => {
    setUploadedFiles((prevFiles) =>
      prevFiles.map((item) =>
        item.id === id ? { ...item, description, isValid } : item
      )
    );
  }, []);

  const handleRemoveFile = useCallback((id: string) => {
    setUploadedFiles((prevFiles) => {
      const removedFile = prevFiles.find(item => item.id === id);
      if (removedFile) {
        URL.revokeObjectURL(removedFile.preview); // Clean up object URL
      }
      return prevFiles.filter(item => item.id !== id);
    });
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      setGlobalError('Please log in to use this feature.');
      return;
    }

    // Validate research insights text
    if (!validateInsight()) {
      return;
    }

    // Check if we have either insights or images
    if (uploadedFiles.length === 0 && insightWordCount < 100) {
      setGlobalError('Please provide either images with descriptions or research insights (minimum 100 words).');
      return;
    }

    // Validate all image descriptions
    const invalidDescriptions = uploadedFiles.filter(file => !file.isValid);
    if (invalidDescriptions.length > 0) {
      setGlobalError('Please fix the errors in image descriptions before submitting.');
      return;
    }

    setIsProcessing(true);
    setProcessingMessage('Preparing your research content...');
    setGlobalError(null);

    try {
      // 1. Build input.tex content
      let insightsContent = cleanTextForSubmission(insightText);
      if (uploadedFiles.length > 0) {
        insightsContent += '\n\n'; // Two newlines to separate from image descriptions
        uploadedFiles.forEach(item => {
          insightsContent += `${item.generatedFileName}: ${cleanTextForSubmission(item.description)}\n`;
        });
      }

      // 2. Create ZIP file
      const zip = new JSZip();
      zip.file('input.tex', insightsContent);
      uploadedFiles.forEach(item => {
        zip.file(item.generatedFileName, item.file); // Add original File object
      });

      const zipBlob = await zip.generateAsync({ type: 'blob' });

      // 3. Upload ZIP to Supabase Edge Function (upload-zip-to-temp)
      setProcessingMessage('Uploading research package...');
      const { data: { session }, error: authError } = await supabase.auth.getSession();
      if (authError || !session) {
        throw new Error('Authentication error during upload.');
      }

      const formData = new FormData();
      // Generate a dynamic filename for the ZIP file
      const dynamicZipFilename = `${user.id}_${Date.now()}_ResBundle.zip`;
      formData.append('file', zipBlob, dynamicZipFilename);

      // Call the new upload-zip-to-temp function
      const uploadUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/upload-zip-to-temp`;
      const uploadResponse = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'X-Function-Slug': 'paper-assistant',
        },
        body: formData,
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json();
        throw new Error(errorData.error || 'File upload failed.');
      }

      const uploadResult = await uploadResponse.json();
      const fileId = uploadResult.fileId; // This is the Supabase Storage path

      setProcessingMessage('File uploaded successfully to storage!');
      console.log('ZIP file uploaded to Supabase Storage:', fileId);
    } catch (err) {
      console.error('Submission failed:', err);
      setGlobalError(err instanceof Error ? err.message : 'An unexpected error occurred during processing.');
      setProcessingMessage(err instanceof Error ? err.message : 'An unexpected error occurred.');
    } finally {
      resetProcessingState();
    }
  };

  useEffect(() => {
    // Revoke object URLs when component unmounts
    return () => {
      uploadedFiles.forEach((file) => URL.revokeObjectURL(file.preview));
    };
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <Link
        to="/dashboard"
        className="inline-flex items-center text-[--text-secondary] hover:text-[--text-primary] mb-8"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back
      </Link>

      <div className="mb-6 sm:mb-8">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <Feather className="w-8 h-8 text-primary-500 mr-3" />
            <h1 className="text-xl sm:text-2xl font-bold text-[--text-primary]">
              Paper Assistant
            </h1>
          </div>
        </div>
        <div className="space-y-2">
          <p className="mt-2 text-sm sm:text-base text-[--text-secondary]">
            Share your research insights and upload supporting images to generate comprehensive academic draft. Provide detailed descriptions for your research ideas and visual materials.
          </p>
          <div className="inline-flex items-center px-3 py-1 rounded-md bg-primary-50 dark:bg-primary-900/30">
            <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
              {creditCost} credits to run this feature
            </span>
          </div>
        </div>
      </div>

      <form onSubmit={onSubmit} className="space-y-8">
        <div className="bg-[--bg-primary] rounded-lg shadow p-4 sm:p-6">
          {globalError && (
            <Alert type="error" className="mb-4">
              {globalError}
            </Alert>
          )}

          {/* Text Input for Research Insights */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <label htmlFor="insightText" className="text-lg font-medium text-[--text-primary]">
                Research Insights & Ideas
              </label>
              <span className={clsx(
                "text-xs font-medium",
                insightWordCount < 100 ? "text-warning-600" : 
                insightWordCount > 1000 ? "text-error-600" : 
                "text-success-600"
              )}>
                {insightWordCount}/1000 words {insightWordCount < 100 && `(${100 - insightWordCount} more needed)`}
              </span>
            </div>
            <textarea
              id="insightText"
              value={insightText}
              onChange={onInsightChange}
              maxLength={10000}
              rows={8}
              className="w-full px-3 py-2 text-sm text-[--text-primary] bg-[--bg-primary] border border-[--border-secondary] rounded-md focus:ring-primary-500 focus:border-primary-500 resize-y"
              placeholder="Describe your research insights, findings, hypotheses, or ideas in detail. Include context about your methodology, key observations, theoretical frameworks, or any significant discoveries. This detailed description will help generate more accurate and relevant academic draft. (Minimum 100 words required)"
            />
            {insightError && (
              <p className="mt-2 text-sm text-error-600">
                {insightError}
              </p>
            )}
            <p className="mt-2 text-xs text-[--text-secondary]">
              Provide comprehensive details about your research to ensure high-quality output. Include background information, methodology, key findings, and implications.
            </p>
          </div>

          {/* Uploaded Images List */}
          {uploadedFiles.length > 0 && (
            <div className="mb-8 space-y-6">
              <h3 className="text-lg font-medium text-[--text-primary]">
                Uploaded Images ({uploadedFiles.length})
              </h3>
              {uploadedFiles.map((item, index) => (
                <UploadedFileItem
                  key={item.id}
                  file={item.file}
                  preview={item.preview}
                  generatedFileName={item.generatedFileName}
                  description={item.description}
                  id={item.id}
                  onDescriptionChange={handleDescriptionChange}
                  onRemove={handleRemoveFile}
                  disabled={isProcessing}
                />
              ))}
            </div>
          )}

          {/* Image Upload Section */}
          <div>
            <h3 className="text-lg font-medium text-[--text-primary] mb-4">
              Supporting Images
            </h3>
            <div
              {...getRootProps()}
              className={clsx(
                "mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md min-h-[120px] cursor-pointer transition-colors",
                isDragActive
                  ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20"
                  : "border-[--border-secondary] hover:border-[--border-primary]",
                isProcessing && "opacity-50 cursor-not-allowed"
              )}
            >
              <input {...getInputProps()} />
              <div className="space-y-1 text-center">
                <Upload className="mx-auto h-12 w-12 text-[--text-disabled]" />
                <div className="flex text-sm text-[--text-secondary]">
                  <span className="relative font-medium text-[--link-default] hover:text-[--link-hover]">
                    Upload files
                  </span>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-[--text-secondary]">
                  PNG or JPEG/JPG up to 1MB each (Max {MAX_FILES} files)
                </p>
                {uploadedFiles.length > 0 && (
                  <p className="text-xs text-[--text-primary] font-medium">
                    {uploadedFiles.length} / {MAX_FILES} files uploaded
                  </p>
                )}
              </div>
            </div>
          </div>

        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isProcessing || (uploadedFiles.length === 0 && insightWordCount < 100) || !user}
            className="flex justify-center py-3 px-6 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isProcessing ? (
              <span className="flex items-center">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Processing...
              </span>
            ) : (
              'Generate Academic Draft'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}